import { Modal, Space, Typography, Radio, Checkbox, Input, message } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { CODIFICATION_QUESTION_TYPE } from '../../../constant/codification';
import { TH5PQuiz } from '../../../models/courses/h5p';
import { extractPlaceholderIndices } from '../utils';

interface H5PQuizModalProps {
  open: boolean;
  quiz: TH5PQuiz | null;
  onSubmit: (value: string | string[]) => void;
  onCancel: () => void;
  allowSkip?: boolean;
}

const H5PQuizModal: React.FC<H5PQuizModalProps> = ({ open, quiz, onSubmit, onCancel, allowSkip = true }) => {
  const { t, i18n } = useTranslation();
  const isRTL = (i18n.dir(i18n.language) || (typeof document !== 'undefined' ? document.dir : 'ltr') || 'ltr') === 'rtl';
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [textValue, setTextValue] = useState('');
  const [fillValues, setFillValues] = useState<string[]>([]);
  const [dragItems, setDragItems] = useState<{ id: string; label: string; isCorrect: boolean }[]>([]);
  const [dropSlots, setDropSlots] = useState<(null | { id: string; label: string; isCorrect: boolean })[]>([]);

  const placeholderOrder = useMemo(() => {
    if (!quiz) {
      return [] as number[];
    }
    const indices = extractPlaceholderIndices(quiz.question);
    if (indices.length === 0 && quiz.questionType === CODIFICATION_QUESTION_TYPE.FILL_IN_THE_BLANKS) {
      return quiz.answers.map((_, index) => index + 1);
    }
    if (indices.length === 0 && quiz.questionType === CODIFICATION_QUESTION_TYPE.DRAG_AND_DROP) {
      return quiz.answers
        .filter((answer) => answer.isCorrect)
        .map((_, index) => index + 1);
    }
    return indices;
  }, [quiz]);

  useEffect(() => {
    if (quiz) {
      setSelectedOptions([]);
      setTextValue('');
      if (quiz.questionType === CODIFICATION_QUESTION_TYPE.FILL_IN_THE_BLANKS) {
        const blankCount = Math.max(quiz.answers.length, placeholderOrder.length || 1);
        setFillValues(Array.from({ length: blankCount }, () => ''));
      } else {
        setFillValues([]);
      }

      if (quiz.questionType === CODIFICATION_QUESTION_TYPE.DRAG_AND_DROP) {
        setDragItems(
          quiz.answers.map((answer) => ({
            id: answer.id,
            label: answer.label,
            isCorrect: !!answer.isCorrect,
          })),
        );
        const slotCount = Math.max(
          quiz.answers.filter((answer) => answer.isCorrect).length,
          placeholderOrder.length,
        );
        setDropSlots(Array.from({ length: slotCount }, () => null));
      } else {
        setDragItems([]);
        setDropSlots([]);
      }
    }
  }, [placeholderOrder.length, quiz, open]);

  if (!quiz) {
    return null;
  }

  const handleDragEnd = (result: DropResult) => {
    if (!quiz || quiz.questionType !== CODIFICATION_QUESTION_TYPE.DRAG_AND_DROP) {
      return;
    }

    const { destination, source, draggableId } = result;
    if (!destination) {
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    let movingItem: { id: string; label: string; isCorrect: boolean } | null = null;
    const nextDragItems = [...dragItems];
    const nextDropSlots = [...dropSlots];

    if (source.droppableId === 'bank') {
      const itemIndex = nextDragItems.findIndex((item) => item.id === draggableId);
      if (itemIndex === -1) {
        return;
      }
      [movingItem] = nextDragItems.splice(itemIndex, 1);
    } else if (source.droppableId.startsWith('slot-')) {
      const slotIndex = Number.parseInt(source.droppableId.replace('slot-', ''), 10);
      const existing = nextDropSlots[slotIndex];
      if (!existing) {
        return;
      }
      movingItem = existing;
      nextDropSlots[slotIndex] = null;
    }

    if (!movingItem) {
      return;
    }

    if (destination.droppableId === 'bank') {
      nextDragItems.splice(destination.index, 0, movingItem);
    } else if (destination.droppableId.startsWith('slot-')) {
      const slotIndex = Number.parseInt(destination.droppableId.replace('slot-', ''), 10);
      const existing = nextDropSlots[slotIndex];
      if (existing) {
        nextDragItems.splice(destination.index, 0, existing);
      }
      nextDropSlots[slotIndex] = movingItem;
    } else {
      return;
    }

    setDragItems(nextDragItems);
    setDropSlots(nextDropSlots);
  };

  const handleConfirm = () => {
    switch (quiz.questionType) {
      case CODIFICATION_QUESTION_TYPE.SINGLE_CHOICE:
        if (selectedOptions.length === 0) {
          message.warning(t('h5p.quizModal.validation.selectAnswer'));
          return;
        }
        onSubmit(selectedOptions[0]);
        break;
      case CODIFICATION_QUESTION_TYPE.MULTI_CHOICE:
        if (selectedOptions.length === 0) {
          message.warning(t('h5p.quizModal.validation.selectMultiple'));
          return;
        }
        onSubmit(selectedOptions);
        break;
      case CODIFICATION_QUESTION_TYPE.FILL_IN_THE_BLANKS: {
        if (fillValues.length === 0 || fillValues.some((value) => !value.trim())) {
          message.warning(t('h5p.quizModal.validation.completeFill'));
          return;
        }
        onSubmit(fillValues.map((value) => value.trim()));
        break;
      }
      case CODIFICATION_QUESTION_TYPE.DRAG_AND_DROP: {
        if (dropSlots.length === 0 || dropSlots.some((slot) => !slot)) {
          message.warning(t('h5p.quizModal.validation.completeDrag'));
          return;
        }
        onSubmit(dropSlots.map((slot) => slot!.id));
        break;
      }
      case CODIFICATION_QUESTION_TYPE.TEXT_INPUT:
      case CODIFICATION_QUESTION_TYPE.NUMERIC_INPUT:
      default:
        if (!textValue.trim()) {
          message.warning(t('h5p.quizModal.validation.enterAnswer'));
          return;
        }
        onSubmit(textValue.trim());
    }
  };

  const renderQuestionContent = () => {
    switch (quiz.questionType) {
      case CODIFICATION_QUESTION_TYPE.SINGLE_CHOICE:
        return (
          <Radio.Group
            value={selectedOptions[0]}
            onChange={(event) => setSelectedOptions([event.target.value])}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {quiz.answers.map((answer) => (
                <Radio value={answer.id} key={answer.id}>
                  {answer.label}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        );
      case CODIFICATION_QUESTION_TYPE.MULTI_CHOICE:
        return (
          <Checkbox.Group
            value={selectedOptions}
            onChange={(values) => setSelectedOptions(values as string[])}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {quiz.answers.map((answer) => (
                <Checkbox value={answer.id} key={answer.id}>
                  {answer.label}
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        );
      case CODIFICATION_QUESTION_TYPE.FILL_IN_THE_BLANKS: {
        const parts = quiz.question.split(/(%\d+)/g);
        return (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Typography.Paragraph type="secondary">
              {t('h5p.quizModal.fillInstruction')}
            </Typography.Paragraph>
            <Typography.Paragraph dir={isRTL ? 'rtl' : 'ltr'}>
              {parts.map((part, index) => {
                const match = part.match(/^%(\d+)$/);
                if (match) {
                  const placeholderIndex = Number.parseInt(match[1], 10);
                  const valueIndex = placeholderOrder.indexOf(placeholderIndex);
                  if (valueIndex === -1) {
                    return <span key={`fill-text-${index}`}>{part}</span>;
                  }

                  const expectedAnswer = quiz.answers[valueIndex]?.label ?? '';
                  const filledValue = fillValues[valueIndex] ?? '';
                  const widthInCharacters = Math.max(
                    expectedAnswer.length,
                    filledValue.length,
                    4,
                  );

                  return (
                    <Input
                      bordered={false}
                      key={`fill-input-${placeholderIndex}-${index}`}
                      value={filledValue}
                      onChange={(event) => {
                        const next = [...fillValues];
                        next[valueIndex] = event.target.value;
                        setFillValues(next);
                      }}
                      style={{
                        display: 'inline-block',
                        width: `${Math.min(Math.max(widthInCharacters, 4), 18)}ch`,
                        minWidth: 80,
                        margin: '0 6px',
                        padding: '2px 0',
                        background: 'transparent',
                        boxShadow: 'none',
                        border: 'none',
                        borderBottom: '2px solid #d9d9d9',
                        borderRadius: 0,
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left',
                        verticalAlign: 'middle',
                      }}
                    />
                  );
                }
                return <span key={`fill-text-${index}`}>{part}</span>;
              })}
            </Typography.Paragraph>
          </Space>
        );
      }
      case CODIFICATION_QUESTION_TYPE.DRAG_AND_DROP: {
        const parts = quiz.question.split(/(%\d+)/g);

        const renderDragQuestion = () => (
          <Typography.Paragraph dir={isRTL ? 'rtl' : 'ltr'}>
            {parts.map((part, index) => {
              const match = part.match(/^%(\d+)$/);
              if (match) {
                const placeholderIndex = Number.parseInt(match[1], 10);
                const slotIndex = placeholderOrder.indexOf(placeholderIndex);
                if (slotIndex === -1) {
                  return <span key={`drag-text-${index}`}>{part}</span>;
                }
                const slotItem = dropSlots[slotIndex];
                return (
                  <Droppable droppableId={`slot-${slotIndex}`} key={`slot-${slotIndex}`}>
                    {(provided, snapshot) => (
                      <span
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{
                          display: 'inline-flex',
                          minWidth: 140,
                          minHeight: 40,
                          margin: '0 6px',
                          padding: '0 8px',
                          borderRadius: 6,
                          border: `1px dashed ${snapshot.isDraggingOver ? '#1677ff' : '#d9d9d9'}`,
                          background: snapshot.isDraggingOver ? '#e6f4ff' : '#fafafa',
                          alignItems: 'center',
                          justifyContent: 'center',
                          direction: isRTL ? 'rtl' : 'ltr',
                        }}
                      >
                        {slotItem ? (
                          <Draggable draggableId={slotItem.id} index={0} key={slotItem.id}>
                            {(dragProvided, dragSnapshot) => (
                              <span
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                style={{
                                  ...dragProvided.draggableProps.style,
                                  display: 'inline-flex',
                                  padding: '4px 12px',
                                  borderRadius: 999,
                                  background: '#1677ff',
                                  color: '#fff',
                                  boxShadow: dragSnapshot.isDragging
                                    ? '0 0 6px rgba(22, 119, 255, 0.4)'
                                    : 'none',
                                  direction: isRTL ? 'rtl' : 'ltr',
                                }}
                              >
                                {slotItem.label}
                              </span>
                            )}
                          </Draggable>
                        ) : (
                          <span style={{ color: '#8c8c8c' }}>
                            {t('h5p.quizModal.dragPlaceholder')}
                          </span>
                        )}
                        {provided.placeholder}
                      </span>
                    )}
                  </Droppable>
                );
              }
              return <span key={`drag-text-${index}`}>{part}</span>;
            })}
          </Typography.Paragraph>
        );

        const renderBank = () => (
          <Droppable droppableId="bank" direction="horizontal">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  padding: 8,
                  borderRadius: 8,
                  border: '1px dashed #d9d9d9',
                  direction: isRTL ? 'rtl' : 'ltr',
                  flexDirection: isRTL ? 'row-reverse' : 'row',
                  justifyContent: isRTL ? 'flex-start' : 'flex-start',
                }}
              >
                {dragItems.map((item, index) => (
                  <Draggable draggableId={item.id} index={index} key={item.id}>
                    {(dragProvided, dragSnapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        style={{
                          ...dragProvided.draggableProps.style,
                          padding: '6px 14px',
                          borderRadius: 999,
                          background: '#1677ff',
                          color: '#fff',
                          boxShadow: dragSnapshot.isDragging
                            ? '0 0 8px rgba(22, 119, 255, 0.45)'
                            : 'none',
                          direction: isRTL ? 'rtl' : 'ltr',
                        }}
                      >
                        {item.label}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        );

        return (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Typography.Paragraph type="secondary">
              {t('h5p.quizModal.dragInstruction')}
            </Typography.Paragraph>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Space direction="vertical" style={{ width: '100%' }}>
                {renderDragQuestion()}
                {renderBank()}
              </Space>
            </DragDropContext>
          </Space>
        );
      }
      case CODIFICATION_QUESTION_TYPE.NUMERIC_INPUT:
        return (
          <Input
            type="number"
            value={textValue}
            onChange={(event) => setTextValue(event.target.value)}
            placeholder={t('h5p.quizModal.placeholders.numeric')}
            style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}
          />
        );
      case CODIFICATION_QUESTION_TYPE.TEXT_INPUT:
      default:
        return (
          <Input
            value={textValue}
            onChange={(event) => setTextValue(event.target.value)}
            placeholder={t('h5p.quizModal.placeholders.text')}
            style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}
          />
        );
    }
  };

  return (
    <Modal
      className="h5p-quiz-modal"
      open={open}
      onCancel={onCancel}
      onOk={handleConfirm}
      okText={t('h5p.quizModal.actions.confirm')}
      cancelText={t('h5p.quizModal.actions.skip')}
      cancelButtonProps={allowSkip ? undefined : { style: { display: 'none' } }}
      maskClosable={false}
      destroyOnClose
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Typography.Title level={4}>{quiz.title || t('h5p.quizModal.defaultTitle')}</Typography.Title>
          {quiz.description && (
            <Typography.Paragraph type="secondary">{quiz.description}</Typography.Paragraph>
          )}
          {/* Only show question text for question types that don't render it inline */}
          {quiz.questionType !== CODIFICATION_QUESTION_TYPE.FILL_IN_THE_BLANKS && 
           quiz.questionType !== CODIFICATION_QUESTION_TYPE.DRAG_AND_DROP && (
            <Typography.Paragraph dir={isRTL ? 'rtl' : 'ltr'}>{quiz.question}</Typography.Paragraph>
          )}
        </div>
        {renderQuestionContent()}
      </Space>
    </Modal>
  );
};

export default H5PQuizModal;
